import { z } from 'zod';
import { identitySchema } from './common.js';
import { affiliateParamsSchema, browserContextSchema, clickIdsSchema, consentSchema, } from './events.js';
// --- Form ID ---
export const formIdSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-zA-Z0-9_-]+$/, 'Alphanumeric, underscore, or hyphen only')
    .meta({
    description: 'Form identifier (alphanumeric, underscore, or hyphen)',
    example: 'peptides-waitlist-q2',
});
// --- Experiment variant link ---
export const experimentVariantLinkSchema = z
    .object({
    experiment_id: z
        .string()
        .min(1)
        .max(200)
        .meta({ description: 'ID of the experiment the visitor was enrolled in' }),
    variant: z
        .string()
        .min(1)
        .max(200)
        .meta({ description: 'Variant the visitor was assigned to' }),
})
    .meta({
    id: 'ExperimentVariantLink',
    description: 'Links a form submission to an active experiment variant',
});
// --- Form submission request ---
export const formSubmissionRequestSchema = z
    .object({
    form_id: formIdSchema,
    identity: identitySchema,
    // Optional metadata
    utm_source: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM source attribution parameter' }),
    utm_medium: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM medium attribution parameter' }),
    utm_campaign: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM campaign attribution parameter' }),
    utm_content: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM content attribution parameter' }),
    utm_term: z
        .string()
        .max(500)
        .optional()
        .meta({ description: 'UTM term attribution parameter' }),
    experiment_variant: experimentVariantLinkSchema
        .optional()
        .meta({ description: 'Link to the experiment variant shown when the form was submitted' }),
    page_url: z
        .string()
        .url()
        .max(2048)
        .optional()
        .meta({ description: 'URL of the page where the form was submitted' }),
    referrer: z.string().max(2048).optional().meta({ description: 'HTTP referrer URL' }),
    // Freeform content
    data: z
        .record(z.string(), z.unknown())
        .refine((obj) => JSON.stringify(obj).length <= 51200, {
        error: 'data must be under 50KB when serialized',
    })
        .meta({ description: 'Freeform form field data (max 50KB serialized)' }),
    // A6: Structured PII — captured raw, hashed at egress to CAPI
    first_name: z
        .string()
        .min(1)
        .max(100)
        .optional()
        .meta({ description: 'First name (raw; hashed before CAPI egress)' }),
    last_name: z
        .string()
        .min(1)
        .max(100)
        .optional()
        .meta({ description: 'Last name (raw; hashed before CAPI egress)' }),
    postal_code: z.string().min(3).max(12).optional().meta({ description: 'Postal/ZIP code' }),
    city: z.string().min(1).max(100).optional().meta({ description: 'City' }),
    state: z.string().min(2).max(100).optional().meta({ description: 'State or province' }),
    country: z
        .string()
        .length(2)
        .regex(/^[A-Z]{2}$/, 'Must be ISO 3166-1 alpha-2 uppercase country code (e.g. US)')
        .optional()
        .meta({ description: 'ISO 3166-1 alpha-2 country code (uppercase, e.g. US)', example: 'US' }),
    date_of_birth: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
        .optional()
        .meta({ description: 'Date of birth in YYYY-MM-DD format (hashed before CAPI egress)' }),
    gender: z.enum(['m', 'f', 'other', 'prefer_not_to_say']).optional().meta({
        description: "Gender for CAPI match-rate. Use 'm', 'f', 'other', or 'prefer_not_to_say'",
    }),
    // A6: Conversion / order fields
    order_id: z
        .string()
        .min(1)
        .max(100)
        .optional()
        .meta({ description: 'Order or transaction ID' }),
    value: z
        .number()
        .min(0)
        .max(1_000_000)
        .optional()
        .meta({ description: 'Monetary value associated with the submission' }),
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/, 'Must be ISO 4217 uppercase currency code (e.g. USD)')
        .optional()
        .meta({ description: 'ISO 4217 currency code (uppercase, e.g. USD)', example: 'USD' }),
    // SDK enrichment fields — sent by the browser SDK alongside the form submission
    event_id: z
        .string()
        .uuid()
        .optional()
        .meta({ description: 'Client-minted UUID for dedup. If omitted, server mints one.' }),
    consent: consentSchema.optional().meta({ description: 'Google Consent Mode v2 signals' }),
    browser: browserContextSchema.optional().meta({ description: 'Browser environment context' }),
    click_ids: clickIdsSchema
        .optional()
        .meta({ description: 'Ad platform click IDs captured from landing page URL' }),
    affiliate_params: affiliateParamsSchema
        .optional()
        .meta({ description: 'Affiliate network params keyed by raw param name' }),
})
    .meta({
    id: 'FormSubmissionRequest',
    description: 'Submit a lead-gen form with identity and freeform data',
});
//# sourceMappingURL=submissions.js.map